import React, { useState, useCallback, useMemo, useEffect } from 'react'

import colorMaps from '../../hooks/useVTK/colorMaps'

import { Space, Select, Slider, Switch, Collapse, Input } from 'antd'

import chroma from 'chroma-js'

import { VTKLayerPropertiesProps } from './VTKLayerProperties.types'

import { InputLabel } from '../InputLabel/InputLabel'
import { PopoverHexPicker } from '../PopoverHexPicker/PopoverHexPicker'
import { VTKDataRangeInput } from '../VTKDataRangeInput'
import { COLORSET_REGEXP, DEFAULT_COLORSET } from 'hooks'

/*
 * Don't love this but it's working pretty well
 * another option would be to flatten data array
 * coming from useVTK
 */
const defaultProperties = {
  hasData: {
    default: false,
    get: (item) => {
      const { pointData, cellData } = item.mapper.inputData
      return pointData.numberOfArrays > 0 || cellData.numberOfArrays > 0
    }
  },
  color: {
    default: '#f0f0f0',
    get: (item) => {
      const [r, g, b] = item.property.diffuseColor
      return chroma.gl(r, g, b).hex()
    }
  },
  visibility: {
    default: true,
    get: (item) => item.visibility
  },
  opacity: {
    default: 1.0,
    get: (item) => item.property.opacity
  },
  representation: {
    default: 'SURFACE',
    get: (item) => item.property.representationAsString
  },
  pointSize: {
    default: 10,
    get: (item) => item.property.pointSize
  },
  edgeVisibility: {
    default: false,
    get: (item) => item.property.edgeVisibility
  },
  edgeColor: {
    default: '#000000',
    get: (item) => item.property.edgeColor
  },
  legendLabel: {
    default: '',
  },
  colorSet: {
    default: DEFAULT_COLORSET,
    get: (item) => item.mapper.colorSet ? item.mapper.colorSet.toLowerCase().replace(COLORSET_REGEXP, '') : undefined
  },
  colorByOptions: {
    default: ['Solid'],
    get: (item) => {
      const { pointData, cellData } = item.mapper.inputData
      const pointNames = pointData.arrays.map(a => a.name)
      const cellNames = cellData.arrays.map(a => a.name)
      return [...pointNames, ...cellNames]
    }
  },
  colorBy: {
    default: 'Solid',
    get: (item) => item.mapper.colorByArrayName ?? 'Solid'
  },
  legend: {
    default: undefined,
    get: (item) => item.legend
  }
} as { [index: string]: any }

export const VTKLayerProperties: React.FunctionComponent<VTKLayerPropertiesProps> = ({ state, ids, dispatch }) => {

  const [activeKey, setActiveKey] = useState<string | string[] | undefined>('display')

  const layers = useMemo(() => state ? state.scene.filter(item => ids.includes(item.id)) : [],[ids, state])

  const getProperty = useCallback((property: string) => (
    layers.reduce((p: any[], c: any) => {
      if (!defaultProperties[property].get) return [defaultProperties[property]['default']]
      const val = defaultProperties[property].get(c.state)
      return typeof val !== 'undefined' ? [...p, val] : p
    }, [])
      .filter((v, i, a) => a.indexOf(v) === i)
      .filter((v, i, a) => v !== null && v !== undefined)
  ), [layers])

  // stands for "layer properties", wanted something short
  const lp = useMemo(() => {

    const properties = Object.keys(defaultProperties).reduce((p: { [index: string]: any }, key) => {
      const prop = getProperty(key)
      if (prop.length === 1) p[key] = prop[0]
      else p[key] = defaultProperties[key].default
      return p
    }, { ...defaultProperties })

    // the following properties need special treatment
    properties.hasData = getProperty('hasData').includes(true)

    return properties
  }, [getProperty])

  useEffect(() => {
    console.log(lp)
  }, [lp])

  useEffect(() => {
    const newKey = lp.hasData ? 'data' : 'display'
    setActiveKey(newKey)
  }, [lp.hasData])

  const handleToggleLegendNan = (bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'toggle-legend-nan', ids, value: bool })
  }

  const handleToggleLegendVisibility = (bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'toggle-legend-visibility', ids, value: bool })
  }

  return (
    <Collapse 
      activeKey={activeKey} 
      accordion 
      onChange={(val) => setActiveKey(val)}
      ghost
      style={{
        border: '1px solid #d0d7de'
      }}
    >
      <Collapse.Panel 
        key='display' 
        header='Display'
      >
        <Space size={12}
          direction='vertical'
          style={{ width: '100%' }}
        >
          <InputLabel label='Opacity'>
            <Slider min={0}
              max={1.0}
              style={{ width: '100%', margin: 0 }}
              step={0.01}
              disabled={layers.length < 1}
              defaultValue={0.5}
              onChange={(value) => {
                dispatch({ type: 'opacity', ids, value })
              }}
            />
          </InputLabel>
          <InputLabel label='Representation'>
            <Select value={lp.representation}
              onChange={(value) => {
                dispatch({ type: 'representation', ids, value })
              }}
              disabled={layers.length < 1}
              style={{ width: '100%' }}
            >
              <Select.Option value="SURFACE">Surface</Select.Option>
              <Select.Option value="WIREFRAME">Wireframe</Select.Option>
              <Select.Option value="POINTS">Points</Select.Option>
            </Select>
          </InputLabel>
          {lp.representation === 'SURFACE' &&
            <Space>
              <InputLabel label='Edge Visibility'
                style={{ width: '100%' }}
              >
                <Switch checked={lp.edgeVisibility}
                  onChange={(value) => {
                    dispatch({ type: 'edgeVisibility', ids, value })
                  }}
                  disabled={layers.length < 1}
                />
              </InputLabel>
              <InputLabel label='Edge Color'
                style={{ width: '100%' }}
              >
                <PopoverHexPicker color={lp.edgeColor}
                  colorCallback={(value) => dispatch({ type: 'edgeColor', ids, value })}
                  swatchStyle={{ margin: 0 }}
                  disabled={!lp.edgeVisibility || layers.length < 1}
                />
              </InputLabel>
            </Space>
          }
          {lp.representation === 'POINTS' &&
            <InputLabel label='Point Size'>
              <Slider min={0}
                max={20}
                style={{ width: '100%', margin: 0 }}
                step={1}
                disabled={layers.length < 1}
                defaultValue={lp.pointSize}
                onChange={(value) => {
                  dispatch({ type: 'pointSize', ids, value })
                }}
              />
            </InputLabel>
          }
        </Space>
      </Collapse.Panel>
      <Collapse.Panel key='data'
        header='Data'
        collapsible={lp.hasData ? undefined : 'disabled'}
      >
        <Space direction='vertical'
          size={12}
          style={{ width: '100%' }}
        >
          <InputLabel label='Color'>
            {typeof lp.colorBy === 'undefined' || lp.colorBy === 'Solid' ?
              <PopoverHexPicker color={lp.color}
                opacity={lp.opacity}
                colorCallback={(value) => {
                  dispatch({ type: 'color', ids, value })
                }}
                opacityCallback={(value) => {
                  dispatch({ type: 'opacity', ids, value })
                }}
                swatchStyle={{ margin: 0 }}
                disabled={layers.length < 1}
              /> :
              <Select value={lp.colorSet}
                onChange={(value: string) => dispatch({ type: 'color-set', ids, value })}
                style={{ width: '100%' }}
              >
                {colorMaps.rgbPresetNames.map((name: string, i) =>
                  <Select.Option value={name.toLowerCase().replace(COLORSET_REGEXP, '')}
                    key={`${name}-${i}`}
                  >{name}</Select.Option>
                )}
              </Select>
            }
          </InputLabel>
          <InputLabel label='Color By'>
            <Select defaultValue={lp.colorBy}
              onChange={(value: string) => {
                dispatch({ type: 'legend-label', value })
                dispatch({ type: 'color-by', ids, value })
              }}
              style={{ width: '100%' }}
            >
              {lp.colorByOptions.map((val: string, i) =>
                <Select.Option value={val}
                  key={`${val}-${i}`}
                >{val}</Select.Option>
              )}
            </Select>
          </InputLabel>
          {lp.colorBy && lp.colorBy !== 'Solid' &&
            <VTKDataRangeInput state={state}
              ids={ids}
              dispatch={dispatch}
            />
          }
        </Space>
      </Collapse.Panel>
      <Collapse.Panel key='legend'
        header='Legend'
        collapsible={lp.hasData ? undefined : 'disabled'}
      >
        <Space direction='vertical'
          size={12}
          style={{ width: '100%' }}
        >
          <InputLabel label='Set Legend Label'
            span={24}
          >
            <Input
              value={(lp && lp.legend) ? lp.legend.axisLabel.replace(` (${lp.legend.units})`, '') : undefined}
              onChange={(e) => {
                if (!dispatch) return
                dispatch({ type: 'legend-label', ids, value: e.target.value })
              }}
            />
          </InputLabel>
          <InputLabel 
            label='Legend Position' 
            span={24}
          >
            <Select
              style={{ width: '100%' }}
              value={lp.legend ? lp.legend.slot : undefined}
              onSelect={(value) => {
                if (!dispatch) return
                dispatch({ type: 'legend-slot', ids, value})
              }}
            >
              <Select.Option key='RIGHT'
                value='RIGHT'
              >Right</Select.Option>
              <Select.Option key='BOTTOM'
                value='BOTTOM'
              >Bottom</Select.Option>
              <Select.Option key='LEFT'
                value='LEFT'
              >Left</Select.Option>
              <Select.Option key='TOP'
                value='TOP'
              >Top</Select.Option>
            </Select>
          </InputLabel>
          <InputLabel label='Toggle Legend Visibility'
            span={24}
          >
            <Switch 
              checked={
                (lp && lp.legend) ? lp.legend.visibility : undefined
              }
              onChange={handleToggleLegendVisibility}
            />
          </InputLabel>
          <InputLabel label='Toggle Legend Nan'
            span={24}
          >
            <Switch 
              checked={
                (lp && lp.legend) ? lp.legend.drawNanAnnotation : undefined
              }
              onChange={handleToggleLegendNan}
            />
          </InputLabel>
        </Space>
      </Collapse.Panel>
    </Collapse>
  )
}